"use client"

import { useState, useEffect } from "react"
import { DashboardHeader } from "@/components/dashboard-header"
import { DashboardShell } from "@/components/dashboard-shell"
import { Button } from "@/components/ui/button"
import { PlusCircle, Loader2, Search } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { Doctor } from "@/types"
import { supabase } from "@/utils/supabase/client"
import { useOrganization } from "@/contexts/organization-context"
import { DoctorList } from "@/components/doctor-list"
import { DoctorFormModal } from "@/components/doctor-form-modal"

export default function DoctorsPage() {
  const { organization } = useOrganization()
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null)
  const [search, setSearch] = useState("")
  const { toast } = useToast()

  useEffect(() => {
    const fetchDoctors = async () => {
      if (!organization) return
      setIsLoading(true)
      const { data, error } = await supabase
        .from("doctors")
        .select("*")
        .eq("organization_id", organization.id)

      if (error) {
        toast({
          title: "Error fetching doctors",
          description: error.message,
          variant: "destructive",
        })
      } else {
        setDoctors(data)
      }
      setIsLoading(false)
    }

    fetchDoctors()
  }, [organization, toast])

  const handleAddDoctor = async (doctor: Omit<Doctor, 'id'>) => {
    const { data, error } = await supabase.from('doctors').insert([doctor]).select('*').single()
    if (!error && data) {
      setDoctors([...doctors, data])
      setIsModalOpen(false)
      toast({ title: "Doctor added", description: `${doctor.name} has been added.` })
    } else {
      toast({ title: "Error", description: error?.message || "Failed to add doctor", variant: "destructive" })
    }
  }

  const handleEditDoctor = (doctor: Doctor) => {
    setEditingDoctor(doctor)
    setIsModalOpen(true)
  }

  const handleUpdateDoctor = async (doctor: Doctor) => {
    const { data, error } = await supabase.from('doctors').update(doctor).eq('id', doctor.id).select('*').single()
    if (!error && data) {
      setDoctors(doctors.map(d => d.id === data.id ? data : d))
      setIsModalOpen(false)
      setEditingDoctor(null)
      toast({ title: "Doctor updated", description: `${doctor.name}'s information has been updated.` })
    } else {
      toast({ title: "Error", description: error?.message || "Failed to update doctor", variant: "destructive" })
    }
  }

  const handleDeleteDoctor = async (id: string) => {
    const { error } = await supabase.from('doctors').delete().eq('id', id)
    if (!error) {
      setDoctors(doctors.filter(d => d.id !== id))
      toast({ title: "Doctor removed", description: `Doctor has been removed.` })
    } else {
      toast({ title: "Error", description: error?.message || "Failed to delete doctor", variant: "destructive" })
    }
  }

  const handleModalClose = () => {
    setEditingDoctor(null)
    setIsModalOpen(false)
  }

  // Search filter
  const filteredDoctors = doctors.filter(
    (d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.phone.toLowerCase().includes(search.toLowerCase()) ||
      d.specialization.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="md:ml-64">
      <DashboardShell>
        <DashboardHeader heading="Doctors" text="Manage your clinic's doctors">
          <div className="flex gap-2 w-full max-w-md">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by name, phone, or specialization..."
                className="w-full border rounded px-3 py-2 pl-9 text-sm"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            </div>
            <Button onClick={() => { setEditingDoctor(null); setIsModalOpen(true); }}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Doctor
            </Button>
          </div>
        </DashboardHeader>
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <DoctorList 
            doctors={filteredDoctors} 
            onEdit={handleEditDoctor}
            onDelete={handleDeleteDoctor}
          />
        )}
        <DoctorFormModal
          isOpen={isModalOpen} 
          onClose={handleModalClose}
          onDoctorCreated={handleAddDoctor}
          onDoctorUpdated={handleUpdateDoctor}
          doctorToEdit={editingDoctor}
        />
      </DashboardShell>
    </div>
  )
}